import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { useContext, useDeferredValue, useEffect, useMemo, useState } from "react";
import {
	Card,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	Icon,
	MenuItem,
	Select,
	Tab,
	Tabs,
	Tooltip,
} from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import SinDatos from "components/Carteles/SinDatos";
import dataCompras from "./data";
import { gql, useLazyQuery, useQuery, useMutation } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import CustomPagination from "components/Pagination";
import ListadoProductos from "./components/listadoProductos";
import CalendarioEntregas from "./components/CalendarioEntregas";
import NoAprobado from "./components/cartelNoAprobado";
import SoftBadge from "components/SoftBadge";
import NoAprobado2 from "./components/cartelNoAprobado2";
import EntregaPDF from "layouts/PDF/compras/entrega";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import ModalRemitos from "layouts/materiaPrima/components/ModalIngresar/ModalRemitos";
import { MessageManager } from "context";
import PagoProveedorSubscriptions from "components/Subscriptions/PagoProveedorSubscriptions";
import { ESTADOS } from "layouts/pagos/config";
import { ConfirmarContext } from "context/ConfirmarContext";
import DinamicModal from "components/Modals/DinamicModal";
import CotizacionCard from "./components/CotizacionCard";
import CotizacionDrawer from "./components/CotizacionDrawer";
import FacturacionTab from "./components/FacturacionTab";

const colores = {
	"No iniciada": "secondary",
	Programada: "info",
	"En curso": "uproPink",
	Cerrada: "secondary",
	Finalizada: "uproGreen",
};

const ESTADOS_LICITACION = ["No iniciada", "Programada", "En curso", "Cerrada", "Finalizada"];
const ESTADOS_PROVEEDOR = ["Ganado", "Licitado", "Sin cotizar"];
const SELECT_ALL_VALUE = "__todos__";
const filterLabelSx = {
	position: "absolute",
	top: -7,
	left: 12,
	zIndex: 2,
	px: 0.5,
	lineHeight: 1,
	backgroundColor: "#ffffff",
};

const renderMultipleFilterValue = (selected, options, label) => {
	if (!selected.length) return "Ninguno seleccionado";
	if (selected.length === options.length) return `Todos los ${label.toLowerCase()} (${options.length})`;
	if (selected.length === 1) return selected[0];
	return `${selected.length} seleccionados`;
};

const GET_SOLICITUDES_ENTREGA = gql`
	query solicitudesEntregaProveedor($idProveedor: ID, $estadoEmail: String, $limit: Int, $offset: Int) {
		solicitudesEntregaProveedor(idProveedor: $idProveedor, estadoEmail: $estadoEmail, limit: $limit, offset: $offset) {
			solicitudes {
				id
				fechaEntrega
				horaEntregaInicio
				horaEntregaFin
				observaciones
				createdAt
				estadoEmail
				errorEmail
				detalles {
					nombre
					cantidad
					unidad
				}
				proveedor {
					id
					razonSocial
					nombre
					apellido
					documento
					user {
						email
					}
				}
				provision {
					id
					estado
					fechaVencimiento
					total
					proveedor {
						id
						razonSocial
						nombre
						apellido
						documento
					}
					pedidoCompra {
						id
						nombre
						pliegoVencimientoDias
						pliegoParticularidades
						createdUser {
							nombre
							apellido
							telefono
						}
					}
					detalles {
						id
						cantidad
						cantidadEntregada
						materiaPrima {
							id
							tipo
							categoria
							nombre
							precio
							tipoUnidad {
								id
								nombre
								abreviatura
								multiplicador
							}
						}
						materiaPrimaRecepcion {
							id
							tipo
							categoria
							nombre
							precio
							tipoUnidad {
								id
								nombre
								abreviatura
								multiplicador
							}
						}
					}
				}
			}
			total
		}
	}
`;

const GET_PROVISIONES = gql`
	query getProvisiones($filter: JSON, $orderFromToday: Boolean, $pagination: PaginationInput) {
		provisiones(filter: $filter, orderFromToday: $orderFromToday, pagination: $pagination) {
			id
			total
			fechaVencimiento
			anulada
			motivoAnulacion
			enviado
			fechaEnvio
			motivoProrroga
			estado
			pedidoCompra {
				id
				nombre
				tipoCompra
				tipoDetalle
				numeroExpediente
				objetoCompra
				pliegoRequiereDemanda
			}
			proveedor {
				id
				tipoProveedor
				razonSocial
				nombre
				apellido
				documento
			}
			detalles {
				id
				cantidad
				cantidadEntregada
				materiaPrima {
					id
					tipo
					categoria
					nombre
					precio
					tipoUnidad {
						id
						nombre
						abreviatura
						multiplicador
					}
				}
				materiaPrimaRecepcion {
					id
					tipo
					categoria
					nombre
					precio
					tipoUnidad {
						id
						nombre
						abreviatura
						multiplicador
					}
				}
				precioUnitario
				subtotal
			}
			historial {
				fecha
				evento
				motivo
				user {
					id
					nombre
					apellido
				}
			}
			ingresos {
				id
				tipo
				fechaEntrega
				movimientoDetalles {
					id
					cantidad
					materiaPrima {
						nombre
						categoria
						codigo
					}
					idProvisionDetalle
				}
				pagosProveedor {
					id
					filePathFactura
					filePathComprobante
					estado
					pagado
					fechaEstimadaPago
					fechaPago
					formaPago
					cuenta
					observacion
					numeroFactura
					motivoRechazo
				}
			}
			pagos {
				id
				tramite
				estado
				fechaFactura
				tipoFactura
				numeroFactura
				fechaPago
				importe
				retencio_iibb
				retencion_ganancias
				total
			}
			solicitudesEntregaProveedor {
				id
				fechaEntrega
				horaEntregaInicio
				horaEntregaFin
				observaciones
				createdAt
				detalles {
					idProvisionDetalle
					nombre
					cantidad
					unidad
				}
			}
		}
	}
`;

const GET_PROVISIONES_FACTURACION = gql`
	query getProvisionesFacturacion($filter: JSON, $pagination: PaginationInput, $order: Order, $search: String) {
		paginationInfo {
			pages
			total
		}
		provisiones(filter: $filter, pagination: $pagination, order: $order, search: $search) {
			id
			pedidoCompra {
				id
				nombre
			}
			ingresos {
				id
				tipo
				fechaEntrega
				observaciones
				movimientoDetalles {
					id
				}
				pagosProveedor {
					id
					filePathFactura
					filePathComprobante
					estado
					pagado
					fechaEstimadaPago
					fechaPago
					motivoRechazo
				}
			}
		}
	}
`;

const CREATE_PAGO_PROVEEDOR = gql`
	mutation createPagoProveedor($input: PagoProveedorInput!) {
		createPagoProveedor(input: $input) {
			id
			idRemito
			filePathFactura
			estado
			observacion
			pagado
			createdAt
			updatedAt
			remito {
				id
			}
		}
	}
`;

const DELETE_PAGO_PROVEEDOR_FILE = gql`
	mutation deletePagoProveedorFile($idRemito: Int!) {
		deletePagoProveedorFile(idRemito: $idRemito)
	}
`;

const GET_PEDIDOS = gql`
	query getPedidosLicitaciones(
		$pagination: PaginationInput
		$order: [Order]
		$search: String
		$filter: JSON
		$idProveedor: ID
		$arrayCategorias: [String]
		$estadosLicitacion: [String]
		$estadosProveedor: [String]
	) {
		paginationInfo {
			pages
			total
		}
		pedidosCompra(
			pagination: $pagination
			order: $order
			search: $search
			filter: $filter
			arrayCategorias: $arrayCategorias
			idProveedor: $idProveedor
			estadosLicitacion: $estadosLicitacion
			estadosProveedor: $estadosProveedor
		) {
			id
			nombre
			subtotalEstimado
			licitacionInicio
			licitacionFin
			licitacionEstado
			puedeCotizar
			numeroExpediente
			tipoCompra
			categorias {
				nombre
			}
			detalles {
				id
				numero
				nombreCompra
				unidadNombre
				unidadAbreviatura
				materiaPrima {
					id
					nombre
					nombreCompra
					codigo
					categoria
					tipo
					descripcion
					propiedades {
						name
						value
					}
					tipoUnidad {
						id
						nombre
						multiplicador
						abreviatura
					}
				}
				observacion
				nombreServicio
				rubroServicio
				cantidad
				cantidadVerificada
				observacionVerificacion
				file
				requierePack
				unidadPack
				totalMP
				equivalenciaPack {
					packs
					materiaPrima
				}
			}
			cotizaciones(idProveedor: $idProveedor) {
				id
				idProveedor
				file
				detalles {
					materiaPrima {
						id
						nombre
						nombreCompra
						codigo
						categoria
						tipo
						descripcion
						propiedades {
							name
							value
						}
						tipoUnidad {
							id
							nombre
							multiplicador
							abreviatura
						}
					}
					nombre
					nombreCompra
					requierePack
					unidad
					unidadPack
					observacion
					precio
					nombreAlternativo
					materiaPrimaAlternativo
					idPedidoCompraDetalle
					plazoEntrega
				}
			}
			provisiones(idProveedor: $idProveedor) {
				id
				anulada
				detalles {
					idPedidoCompraDetalle
					precioUnitario
					cantidad
					materiaPrima {
						nombre
					}
				}
				proveedor {
					id
				}
			}
			responsable {
				nombre
				apellido
				email
				telefono
			}
		}
	}
`;

function Licitaciones() {
	const [selected, setSelected] = useState(null);
	const [drawerCotizacion, setDrawerCotizacion] = useState(null);
	const [activeSectionTab, setActiveSectionTab] = useState(0);
	const { columns, getRows } = dataCompras;
	const [rows, setRows] = useState([]);
	const [search, setSearch] = useState("");
	const deferredSearch = useDeferredValue(search.trim());
	const [selectedRubros, setSelectedRubros] = useState(null);
	const [selectedEstadosLicitacion, setSelectedEstadosLicitacion] = useState(ESTADOS_LICITACION);
	const [selectedEstadosProveedor, setSelectedEstadosProveedor] = useState(ESTADOS_PROVEEDOR);
	const [page, setPage] = useState(1);
	const [openNoAprobado2, setOpenNoAprobado2] = useState(false);
	const [modalCerrado, setModalCerrado] = useState(false);
	const [pdfData, setPdfData] = useState(null);
	const solicitudesLimit = 200;
	const solicitudesOffset = 0;
	const { user } = useContext(UserContext);
	const [openModalRemitos, setOpenModalRemitos] = useState(false);
	const [idProvision, setIdProvision] = useState(null);
	const [selectedProvision, setSelectedProvision] = useState();
	const [expandedProvisions, setExpandedProvisions] = useState({});
	const [facturacionFilters, setFacturacionFilters] = useState({ search: "", estado: "", fecha: "" });
	const deferredFacturacionSearch = useDeferredValue(facturacionFilters.search.trim());
	const [facturacionPage, setFacturacionPage] = useState(1);
	const { handleSnackbar } = useContext(MessageManager);
	const { confirmar } = useContext(ConfirmarContext);
	const [remitoRechazado, setRemitoRechazado] = useState(null);

	const [pagesInfo, setPagesInfo] = useState({
		pages: 0,
		total: 0,
	});

	const [order, setOrder] = useState({
		campo: "id",
		orden: "DESC",
	});

	const { data: dataProveedor, loading: loadingProveedores } = useQuery(
		gql`
			query proveedorUserLicitaciones {
				proveedorUser {
					id
					listadoFiles
					cbu
					titular
					banco
					alias
					categorias {
						nombre
					}
					habilitado
				}
			}
		`,
		{ fetchPolicy: "network-only" }
	);

	const proveedorRubros = useMemo(
		() => dataProveedor?.proveedorUser?.categorias?.map((item) => item.nombre).filter(Boolean).sort() || [],
		[dataProveedor]
	);

	useEffect(() => {
		if (dataProveedor) {
			setSelectedRubros(proveedorRubros);
		}
	}, [dataProveedor, proveedorRubros]);

	const [getPedidos, { data, loading, refetch }] = useLazyQuery(GET_PEDIDOS, {
		fetchPolicy: "network-only",
	});

	const {
		data: dataProvisiones,
		loading: loadingProvisiones,
		refetch: refetchProvisiones,
	} = useQuery(GET_PROVISIONES, {
		variables: {
			filter: {
				idProveedor: dataProveedor?.proveedorUser?.id || null,
			},
		},
		skip: !dataProveedor?.proveedorUser?.id,
		fetchPolicy: "cache-and-network",
	});

	const facturacionDateRange = useMemo(() => {
		const hoy = dayjs();
		switch (facturacionFilters.fecha) {
			case "hoy":
				return { fechaDesde: hoy.format("YYYY-MM-DD"), fechaHasta: hoy.format("YYYY-MM-DD") };
			case "ultimos7":
				return { fechaDesde: hoy.subtract(6, "day").format("YYYY-MM-DD"), fechaHasta: hoy.format("YYYY-MM-DD") };
			case "ultimos30":
				return { fechaDesde: hoy.subtract(29, "day").format("YYYY-MM-DD"), fechaHasta: hoy.format("YYYY-MM-DD") };
			case "anioActual":
				return { fechaDesde: hoy.startOf("year").format("YYYY-MM-DD"), fechaHasta: hoy.endOf("year").format("YYYY-MM-DD") };
			default:
				return {};
		}
	}, [facturacionFilters.fecha]);

	const {
		data: dataFacturacion,
		loading: loadingFacturacion,
		refetch: refetchFacturacion,
	} = useQuery(GET_PROVISIONES_FACTURACION, {
		variables: {
			pagination: { page: facturacionPage, limit: 10 },
			order: { field: "id", order: "DESC" },
			search: deferredFacturacionSearch || null,
			filter: {
				idProveedor: dataProveedor?.proveedorUser?.id || null,
				...(facturacionFilters.estado ? { estadoPagoProveedor: facturacionFilters.estado } : {}),
				...facturacionDateRange,
				...(Object.keys(facturacionDateRange).length ? { tipoFecha: "entrega" } : {}),
			},
		},
		skip: !dataProveedor?.proveedorUser?.id,
		// paginationInfo es un campo raíz compartido; no almacenarlo evita que
		// otra consulta paginada reemplace el total de Facturación en Apollo.
		fetchPolicy: "no-cache",
	});

	const { data: dataSolicitudes, loading: loadingSolicitudes } = useQuery(GET_SOLICITUDES_ENTREGA, {
		variables: {
			idProveedor: dataProveedor?.proveedorUser?.id || null,
			estadoEmail: null,
			limit: solicitudesLimit,
			offset: solicitudesOffset,
		},
		skip: !dataProveedor?.proveedorUser?.id,
		fetchPolicy: "cache-and-network",
	});

	const [createPagoProveedor, { loading: loadingPagoProveedor }] = useMutation(CREATE_PAGO_PROVEEDOR);
	const [deletePagoProveedorFileMutation, { loading: loadingDeletePagoProveedor }] = useMutation(DELETE_PAGO_PROVEEDOR_FILE);

	const loadingFactura = loadingPagoProveedor || loadingDeletePagoProveedor;

	// Función para actualizar datos cuando hay cambios
	const handlePagoProveedorUpdate = (action, data) => {
		refetchProvisiones();
		refetchFacturacion();
	};

	useEffect(() => {
		const sessionId = sessionStorage.getItem("noAprobadoDialogShownLicitaciones");
		if (!sessionId) {
			setOpenNoAprobado2(true);
			sessionStorage.setItem("noAprobadoDialogShownLicitaciones", "true");
		}
	}, []);

	useEffect(() => {
		if (!dataProveedor || selectedRubros === null) return;

		if (proveedorRubros.length === 0) {
			setRows([]);
			setPagesInfo({ pages: 0, total: 0 });
			return;
		}

		getPedidos({
			variables: {
				pagination: {
					page,
					limit: 5,
				},
				order: [{ field: order.campo, order: order.orden }],
				search: deferredSearch,
				filter: {
					licitacionInicio: {
						_not: null,
					},
					estado: { _ne: "ANULADO" },
				},
				arrayCategorias: selectedRubros.length ? selectedRubros : ["__sin_rubros_seleccionados__"],
				idProveedor: dataProveedor?.proveedorUser?.id,
				estadosLicitacion:
					selectedEstadosLicitacion.length === ESTADOS_LICITACION.length
						? undefined
						: selectedEstadosLicitacion,
				estadosProveedor:
					selectedEstadosProveedor.length === ESTADOS_PROVEEDOR.length
						? undefined
						: selectedEstadosProveedor,
			},
		});
	}, [
		dataProveedor,
		page,
		order,
		deferredSearch,
		selectedRubros,
		selectedEstadosLicitacion,
		selectedEstadosProveedor,
	]);

	useEffect(() => {
		if (data) {
			setRows(getRows(data?.pedidosCompra, setSelected, dataProveedor?.proveedorUser?.id, colores));
			setPagesInfo(data.paginationInfo);
		}
	}, [data]);

	const handleMultipleFilterChange = (event, options, setter) => {
		const rawValue = event.target.value;
		const values = typeof rawValue === "string" ? rawValue.split(",") : rawValue;
		const nextValues = values.includes(SELECT_ALL_VALUE)
			? values.filter((value) => value !== SELECT_ALL_VALUE).length === options.length
				? []
				: options
			: values;

		setter(nextValues);
		setPage(1);
	};

	const handleResetFilters = () => {
		setSearch("");
		setSelectedRubros(proveedorRubros);
		setSelectedEstadosLicitacion(ESTADOS_LICITACION);
		setSelectedEstadosProveedor(ESTADOS_PROVEEDOR);
		setPage(1);
	};

	const filtersAreActive =
		Boolean(search.trim()) ||
		(selectedRubros?.length ?? 0) !== proveedorRubros.length ||
		selectedEstadosLicitacion.length !== ESTADOS_LICITACION.length ||
		selectedEstadosProveedor.length !== ESTADOS_PROVEEDOR.length;

	const handleOrder = (campo) => {
		if (order.campo === campo) {
			setOrder({
				campo: campo,
				orden: order.orden === "ASC" ? "DESC" : "ASC",
			});
		} else {
			setOrder({
				campo: campo,
				orden: "ASC",
			});
		}
	};

	const handleCloseModalNoAprobado = () => {
		setModalCerrado(true);
		setOpenNoAprobado2(false);
	};

	const handleGeneratePdf = (solicitud) => {
		setPdfData({ ...solicitud, user });
	};

	const handlePdfGenerated = () => {
		setPdfData(null);
	};

	const handleOpenRemitos = (provision) => {
		if (!provision) {
			return;
		}

		setIdProvision(provision.id);
		setSelectedProvision(provision);
		setOpenModalRemitos(true);
	};

	const solicitudes = dataSolicitudes?.solicitudesEntregaProveedor?.solicitudes || [];

	const toggleProvisionExpansion = (provisionId) => {
		setExpandedProvisions((prev) => ({
			...prev,
			[provisionId]: !prev[provisionId],
		}));
	};

	const getPagoProveedorFactura = (remito) => remito?.pagosProveedor;
	const hasFacturaAdjunta = (remito) => Boolean(getPagoProveedorFactura(remito)?.filePathFactura);
	const hasComprobantePagoAdjunto = (remito) => Boolean(getPagoProveedorFactura(remito)?.filePathComprobante);
	const hasMotivoRechazoFactura = (remito) => {
		const motivo = getPagoProveedorFactura(remito)?.motivoRechazo;
		return typeof motivo === "string" && motivo.trim().length > 0;
	};

	const handleAdjuntarFactura = (remito) => {
		if (loadingFactura) return;

		const alreadyHadFactura = hasFacturaAdjunta(remito);

		// Crear un input file invisible
		const fileInput = document.createElement("input");
		fileInput.type = "file";
		fileInput.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
		fileInput.style.display = "none";

		fileInput.onchange = async (event) => {
			const file = event.target.files?.[0];
			if (!file) {
				return;
			}

			const resolvedFile = await new Promise((resolve) => {
				if (file.size <= 2000000) {
					resolve(file);
				} else {
					resolve(null);
				}
			});

			if (!resolvedFile) {
				handleSnackbar("El archivo debe tener un tamaño máximo de 2MB.", "warning");
				return;
			}

			try {
				await createPagoProveedor({
					variables: {
						input: {
							idRemito: Number(remito.id),
							fileUpload: resolvedFile,
						},
					},
				});

				await Promise.all([refetchProvisiones(), refetchFacturacion()]);
				handleSnackbar(alreadyHadFactura ? "Factura actualizada correctamente." : "Factura adjuntada correctamente.", "success");
			} catch (error) {
				console.error("Error al adjuntar factura:", error);
				handleSnackbar("Error al adjuntar la factura. Intenta nuevamente.", "error");
			}
		};

		// Simular click en el input file
		document.body.appendChild(fileInput);
		fileInput.click();
		document.body.removeChild(fileInput);
	};

	const handleVerRemito = (remito) => {
		if (!remito?.id) {
			handleSnackbar("No se encontro el remito para visualizar.", "error");
			return;
		}

		if (typeof window === "undefined") {
			return;
		}

		window.open(`/remitoIndividual/pdf/${remito.id}`, "_blank", "noopener,noreferrer");
	};

	const handleVerFactura = (remito) => {
		const facturaPath = getPagoProveedorFactura(remito)?.filePathFactura;

		if (!facturaPath) {
			handleSnackbar("El remito no tiene una factura cargada.", "warning");
			return;
		}

		if (typeof window === "undefined") {
			return;
		}

		if (facturaPath.startsWith("http")) {
			window.open(facturaPath, "_blank", "noopener,noreferrer");
			return;
		}

		const sanitizedPath = facturaPath.startsWith("/") ? facturaPath.slice(1) : facturaPath;
		const url = resolveStoredFileUrl(sanitizedPath);
		window.open(url, "_blank", "noopener,noreferrer");
	};

	const handleVerComprobantePago = (remito) => {
		const comprobantePath = getPagoProveedorFactura(remito)?.filePathComprobante;

		if (!comprobantePath) {
			handleSnackbar("El remito no tiene comprobante de pago cargado.", "warning");
			return;
		}

		if (typeof window === "undefined") {
			return;
		}

		if (comprobantePath.startsWith("http")) {
			window.open(comprobantePath, "_blank", "noopener,noreferrer");
			return;
		}

		const sanitizedPath = comprobantePath.startsWith("/") ? comprobantePath.slice(1) : comprobantePath;
		const url = resolveStoredFileUrl(sanitizedPath);
		window.open(url, "_blank", "noopener,noreferrer");
	};

	const handleEliminarFactura = async (remito) => {
		if (loadingFactura) return;

		const facturaPath = getPagoProveedorFactura(remito)?.filePathFactura;

		if (!facturaPath) {
			handleSnackbar("Este remito no tiene factura para eliminar.", "warning");
			return;
		}

		try {
			await deletePagoProveedorFileMutation({
				variables: {
					idRemito: Number(remito.id),
				},
			});

			await Promise.all([refetchProvisiones(), refetchFacturacion()]);
			handleSnackbar("Factura eliminada correctamente.", "success");
		} catch (error) {
			console.error("Error al eliminar factura:", error);
			handleSnackbar("Error al eliminar la factura. Intenta nuevamente.", "error");
		}
	};

	// Columnas para la tabla de remitos
	const remitosColumns = [
		{
			field: "id",
			headerName: "N° Remito",
			// width: "15%",
			align: "center",
			renderCell: (remito) => (
				<SoftTypography variant="caption" fontWeight="bold">
					{remito.id || 0}
				</SoftTypography>
			),
			renderHeader: (column, isCurrentSort, direction) => (
				<SoftBox
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						width: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<SoftTypography
						variant="caption"
						fontWeight="bold"
						textTransform="uppercase"
						color="dark"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							flex: 1,
							minWidth: 0,
						}}
						title={column.headerName}
					>
						{column.headerName}
					</SoftTypography>
				</SoftBox>
			),
		},
		{
			field: "fechaEntrega",
			headerName: "Fecha de entrega",
			// width: "20%",
			align: "center",
			renderCell: (remito) => {
				return (
					<SoftTypography variant="caption" fontWeight="bold">
						{remito.fechaEntrega ? dayjs(remito.fechaEntrega).format("DD/MM/YYYY") : "-"}
					</SoftTypography>
				);
			},
			renderHeader: (column, isCurrentSort, direction) => (
				<SoftBox
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						width: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<SoftTypography
						variant="caption"
						fontWeight="bold"
						textTransform="uppercase"
						color="dark"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							flex: 1,
							minWidth: 0,
						}}
						title={column.headerName}
					>
						{column.headerName}
					</SoftTypography>
				</SoftBox>
			),
		},
		{
			field: "items",
			headerName: "Items",
			// width: "15%",
			align: "center",
			renderCell: (remito) => (
				<SoftTypography variant="caption" fontWeight="bold">
					{remito.movimientoDetalles?.length || 0}
				</SoftTypography>
			),
			renderHeader: (column, isCurrentSort, direction) => (
				<SoftBox
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						width: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<SoftTypography
						variant="caption"
						fontWeight="bold"
						textTransform="uppercase"
						color="dark"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							flex: 1,
							minWidth: 0,
						}}
						title={column.headerName}
					>
						{column.headerName}
					</SoftTypography>
				</SoftBox>
			),
		},
		{
			field: "observaciones",
			headerName: "Observaciones",
			align: "left",
			renderCell: (remito) => (
				<SoftTypography variant="caption" fontWeight="bold">
					{remito.observaciones || "-"}
				</SoftTypography>
			),
			renderHeader: (column, isCurrentSort, direction) => (
				<SoftBox
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						width: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<SoftTypography
						variant="caption"
						fontWeight="bold"
						textTransform="uppercase"
						color="dark"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							flex: 1,
							minWidth: 0,
						}}
						title={column.headerName}
					>
						{column.headerName}
					</SoftTypography>
				</SoftBox>
			),
		},
		{
			field: "estado",
			headerName: "Estado factura",
			align: "left",
			renderCell: (remito) => {
				const hasPagosProveedor = remito?.pagosProveedor;

				return hasPagosProveedor ? (
					<SoftBox
						display="flex"
						alignItems="center"
						gap={1}
						sx={{
							width: "100%",
							overflow: "hidden",
							minWidth: 0,
						}}
						ml={ESTADOS[remito?.pagosProveedor?.estado]?.margin}
					>
						<SoftBadge
							color={ESTADOS[remito?.pagosProveedor?.estado]?.color || "info"}
							badgeContent={ESTADOS[remito?.pagosProveedor?.estado]?.label || "-"}
						/>
					</SoftBox>
				) : (
					<SoftBox
						display="flex"
						alignItems="center"
						gap={1}
						sx={{
							width: "100%",
							overflow: "hidden",
							minWidth: 0,
						}}
						ml={ESTADOS[remito?.pagosProveedor?.estado]?.margin}
					>
						<SoftBadge color="uproPink" badgeContent={"Sin facturar"} />
					</SoftBox>
				);
			},
			renderHeader: (column, isCurrentSort, direction) => (
				<SoftBox
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						width: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<SoftTypography
						variant="caption"
						fontWeight="bold"
						textTransform="uppercase"
						color="dark"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							flex: 1,
							minWidth: 0,
						}}
						title={column.headerName}
					>
						{column.headerName}
					</SoftTypography>
				</SoftBox>
			),
		},
		{
			field: "pago",
			headerName: "Fecha de pago",
			align: "left",
			renderCell: (remito) => (
				<SoftBox
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						width: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
					ml={2}
				>
					<Tooltip
						title={remito?.pagosProveedor?.fechaEstimadaPago && !remito?.pagosProveedor?.fechaPago ? "Fecha estimada de pago" : ""}
						placement="top"
					>
						<SoftTypography variant="caption" fontWeight="bold">
							{remito?.pagosProveedor?.fechaPago || remito?.pagosProveedor?.fechaEstimadaPago
								? dayjs(remito?.pagosProveedor?.fechaPago || remito?.pagosProveedor?.fechaEstimadaPago).format("DD/MM/YYYY")
								: "Sin pago"}
						</SoftTypography>
					</Tooltip>
				</SoftBox>
			),
			renderHeader: (column, isCurrentSort, direction) => (
				<SoftBox
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						width: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<SoftTypography
						variant="caption"
						fontWeight="bold"
						textTransform="uppercase"
						color="dark"
						sx={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							flex: 1,
							minWidth: 0,
						}}
						title={column.headerName}
					>
						{column.headerName}
					</SoftTypography>
				</SoftBox>
			),
		},
	];

	// Acciones para los remitos
	const remitosActions = [
		{
			icon: "picture_as_pdf",
			tooltip: "Ver remito",
			color: "primary",
			onClick: handleVerRemito,
		},
		{
			icon: "visibility",
			tooltip: "Ver factura",
			color: "info",
			onClick: handleVerFactura,
			hidden: (remito) => !hasFacturaAdjunta(remito),
		},
		{
			icon: "payments",
			tooltip: "Ver comprobante de pago",
			color: "uproGreen",
			onClick: handleVerComprobantePago,
			hidden: (remito) => remito?.pagosProveedor?.estado !== "OPFinalizada" || !hasComprobantePagoAdjunto(remito),
		},
		{
			icon: (remito) => (hasFacturaAdjunta(remito) && remito?.pagosProveedor?.estado !== "Rechazado" ? "drive_folder_upload" : "upload"),
			tooltip: (remito) => (hasFacturaAdjunta(remito) && remito?.pagosProveedor?.estado !== "Rechazado" ? "Cambiar factura" : "Adjuntar factura"),
			color: (remito) => (hasFacturaAdjunta(remito) && remito?.pagosProveedor?.estado !== "Rechazado" ? "warning" : "info"),
			onClick: handleAdjuntarFactura,
			hidden: (remito) => remito?.pagosProveedor && !["Pendiente", "SinFacturar", "Rechazado"].includes(remito?.pagosProveedor?.estado),
		},
		{
			icon: "delete",
			tooltip: "Eliminar factura",
			color: "error",
			onClick: handleEliminarFactura,
			hidden: (remito) =>
				!hasFacturaAdjunta(remito) || (remito?.pagosProveedor && !["Pendiente", "SinFacturar"].includes(remito?.pagosProveedor?.estado)),
		},
		{
			icon: "block",
			tooltip: "Ver motivo del rechazo de la factura",
			color: "uproPink",
			onClick: (remito) => {
				setRemitoRechazado(remito);
			},
			hidden: (remito) => remito?.pagosProveedor?.estado !== "Rechazado" || !hasMotivoRechazoFactura(remito),
		},
	];

	// Obtener provisiones del proveedor
	const provisionesFacturacion = dataFacturacion?.provisiones || [];
	const facturacionPagination = {
		page: facturacionPage,
		pages: Number(dataFacturacion?.paginationInfo?.pages || 0),
		total: Number(dataFacturacion?.paginationInfo?.total || 0),
	};
	const facturacionResumen = useMemo(() => {
		const remitos = (dataProvisiones?.provisiones || []).flatMap((provision) =>
			(provision.ingresos || []).filter((ingreso) => ingreso.tipo === "Remito Proveedor")
		);

		return remitos.reduce(
			(acc, remito) => {
				const pago = remito?.pagosProveedor;
				const estado = pago?.estado || "SinFacturar";
				if (estado === "Pendiente") acc.pendientes += 1;
				if (!pago || ["SinFacturar", "Rechazado"].includes(estado)) acc.sinFacturar += 1;
				if (pago?.pagado || estado === "OPFinalizada") acc.pagadas += 1;
				return acc;
			},
			{ pendientes: 0, sinFacturar: 0, pagadas: 0 }
		);
	}, [dataProvisiones]);
	useEffect(() => {
		const pages = Number(dataFacturacion?.paginationInfo?.pages || 0);
		if (pages > 0 && facturacionPage > pages) setFacturacionPage(pages);
		if (pages === 0 && facturacionPage !== 1 && !loadingFacturacion) setFacturacionPage(1);
	}, [dataFacturacion?.paginationInfo?.pages, facturacionPage, loadingFacturacion]);

	const handleClearFacturacionFilters = () => {
		setFacturacionFilters({ search: "", estado: "", fecha: "" });
		setFacturacionPage(1);
	};

	return (
		<DashboardLayout>
			<DashboardNavbar />
			{/* Suscripciones para actualizaciones en tiempo real */}
			<PagoProveedorSubscriptions onUpdate={handlePagoProveedorUpdate} showNotifications={false} />
			<SoftBox py={3}>
				<SoftBox mb={3}>
					{selected ? (
						<ListadoProductos
							detalles={selected}
							setSelected={setSelected}
							idProveedor={dataProveedor?.proveedorUser?.id}
							refetchParent={refetch}
							colores={colores}
						/>
					) : (
						<>
							<Card sx={{ mb: 2, p: { xs: 1, sm: 1.5 } }}>
								<Tabs
									value={activeSectionTab}
									onChange={(_event, value) => setActiveSectionTab(value)}
									variant="fullWidth"
									aria-label="Secciones de licitaciones"
									sx={{
										"& .MuiTab-root": {
											minHeight: 48,
											minWidth: 0,
											flex: 1,
											flexDirection: { xs: "column", sm: "row" },
											px: { xs: 0.5, sm: 2 },
											textTransform: "none",
											whiteSpace: "nowrap",
											fontSize: { xs: "0.72rem", sm: "0.875rem" },
											fontWeight: 600,
											cursor: "pointer",
											transition: "color 200ms ease, background-color 200ms ease",
										},
										"& .MuiTab-iconWrapper": {
											marginRight: { xs: 0, sm: 1 },
											marginBottom: { xs: 0.25, sm: 0 },
										},
										"& .MuiTab-root:hover": { backgroundColor: "rgba(240, 5, 122, 0.06)" },
										"& .MuiTab-root:focus-visible": {
											outline: "3px solid rgba(240, 5, 122, 0.35)",
											outlineOffset: -3,
										},
									}}
								>
									<Tab
										id="licitaciones-tab-0"
										aria-controls="licitaciones-tabpanel-0"
										icon={<Icon fontSize="small">request_quote</Icon>}
										iconPosition="start"
										label="Cotizaciones"
									/>
									<Tab
										id="licitaciones-tab-1"
										aria-controls="licitaciones-tabpanel-1"
										icon={<Icon fontSize="small">calendar_month</Icon>}
										iconPosition="start"
										label={
											<SoftBox component="span">
												<SoftBox component="span" display={{ xs: "none", sm: "inline" }}>
													Calendario de entregas
												</SoftBox>
												<SoftBox component="span" display={{ xs: "inline", sm: "none" }}>
													Calendario
												</SoftBox>
											</SoftBox>
										}
									/>
									<Tab
										id="licitaciones-tab-2"
										aria-controls="licitaciones-tabpanel-2"
										icon={<Icon fontSize="small">receipt_long</Icon>}
										iconPosition="start"
										label="Facturación"
									/>
								</Tabs>
							</Card>

							<SoftBox
								role="tabpanel"
								id="licitaciones-tabpanel-0"
								aria-labelledby="licitaciones-tab-0"
								hidden={activeSectionTab !== 0}
								sx={{ "&[hidden]": { display: "none" } }}
							>
								<Card sx={{ mb: 2, overflow: "hidden" }}>
									<SoftBox p={{ xs: 2, sm: 3 }} pb={{ xs: 2, sm: 2.5 }}>
										<SoftBox display="flex" alignItems="center" gap={1.5} mb={2.5}>
											<SoftBox
												display="grid"
												sx={{
													placeItems: "center",
													width: 42,
													height: 42,
													borderRadius: "12px",
													backgroundColor: "rgba(240, 5, 122, 0.08)",
													color: "primary.main",
													flex: "0 0 auto",
												}}
											>
												<Icon>request_quote</Icon>
											</SoftBox>
											<SoftBox minWidth={0}>
												<SoftTypography variant="h5" color="dark" fontWeight="bold">
													Cotizaciones
												</SoftTypography>
												<SoftTypography variant="caption" color="text">
													Busca y filtra los pedidos disponibles según tus rubros.
												</SoftTypography>
											</SoftBox>
										</SoftBox>

										<SoftBox
											display="grid"
											gridTemplateColumns={{
												xs: "1fr",
												sm: "repeat(2, minmax(0, 1fr))",
												xl: "minmax(240px, 1.2fr) repeat(3, minmax(185px, 0.8fr)) auto",
											}}
											columnGap={1.5}
											rowGap={{ xs: 2.25, sm: 1.5 }}
											alignItems="center"
										>
											<SoftInput
												value={search}
												onChange={(event) => {
													setSearch(event.target.value);
													setPage(1);
												}}
												placeholder="Buscar por nombre o ID..."
												icon={{ component: <Icon>search</Icon>, direction: "left" }}
												inputProps={{ "aria-label": "Buscar cotizaciones por nombre o ID" }}
											/>

											<SoftBox position="relative">
												<SoftTypography component="label" htmlFor="filtro-rubros" variant="caption" color="dark" sx={filterLabelSx}>
													Rubros
												</SoftTypography>
												<Select
													id="filtro-rubros"
													multiple
													value={selectedRubros || []}
													label="Rubros"
													disabled={!proveedorRubros.length}
													onChange={(event) => handleMultipleFilterChange(event, proveedorRubros, setSelectedRubros)}
													renderValue={(selected) => renderMultipleFilterValue(selected, proveedorRubros, "Rubros")}
													MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
													inputProps={{ "aria-label": "Filtrar por rubros" }}
													sx={{ width: "100%" }}
												>
													<MenuItem value={SELECT_ALL_VALUE}>
														<Checkbox
															checked={Boolean(proveedorRubros.length) && selectedRubros?.length === proveedorRubros.length}
															indeterminate={Boolean(selectedRubros?.length) && selectedRubros.length < proveedorRubros.length}
														/>
														<SoftTypography variant="button" color="dark">Todos los rubros</SoftTypography>
													</MenuItem>
													{proveedorRubros.map((rubro) => (
														<MenuItem key={rubro} value={rubro}>
															<Checkbox checked={selectedRubros?.includes(rubro) || false} />
															<SoftTypography variant="button" color="dark">{rubro}</SoftTypography>
														</MenuItem>
													))}
												</Select>
											</SoftBox>

											<SoftBox position="relative">
												<SoftTypography component="label" htmlFor="filtro-estado-licitacion" variant="caption" color="dark" sx={filterLabelSx}>
													Estado de cotización
												</SoftTypography>
												<Select
													id="filtro-estado-licitacion"
													multiple
													value={selectedEstadosLicitacion}
													label="Estado de cotización"
													onChange={(event) =>
														handleMultipleFilterChange(event, ESTADOS_LICITACION, setSelectedEstadosLicitacion)
													}
													renderValue={(selected) => renderMultipleFilterValue(selected, ESTADOS_LICITACION, "Estados")}
													MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
													inputProps={{ "aria-label": "Filtrar por estado de cotización" }}
													sx={{ width: "100%" }}
												>
													<MenuItem value={SELECT_ALL_VALUE}>
														<Checkbox checked={selectedEstadosLicitacion.length === ESTADOS_LICITACION.length} />
														<SoftTypography variant="button" color="dark">Todos los estados</SoftTypography>
													</MenuItem>
													{ESTADOS_LICITACION.map((estado) => (
														<MenuItem key={estado} value={estado}>
															<Checkbox checked={selectedEstadosLicitacion.includes(estado)} />
															<SoftTypography variant="button" color="dark">{estado}</SoftTypography>
														</MenuItem>
													))}
												</Select>
											</SoftBox>

											<SoftBox position="relative">
												<SoftTypography component="label" htmlFor="filtro-estado-proveedor" variant="caption" color="dark" sx={filterLabelSx}>
													Mi estado
												</SoftTypography>
												<Select
													id="filtro-estado-proveedor"
													multiple
													value={selectedEstadosProveedor}
													label="Mi estado"
													onChange={(event) =>
														handleMultipleFilterChange(event, ESTADOS_PROVEEDOR, setSelectedEstadosProveedor)
													}
													renderValue={(selected) => renderMultipleFilterValue(selected, ESTADOS_PROVEEDOR, "Estados")}
													MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
													inputProps={{ "aria-label": "Filtrar por mi estado" }}
													sx={{ width: "100%" }}
												>
													<MenuItem value={SELECT_ALL_VALUE}>
														<Checkbox checked={selectedEstadosProveedor.length === ESTADOS_PROVEEDOR.length} />
														<SoftTypography variant="button" color="dark">Todos mis estados</SoftTypography>
													</MenuItem>
													{ESTADOS_PROVEEDOR.map((estado) => (
														<MenuItem key={estado} value={estado}>
															<Checkbox checked={selectedEstadosProveedor.includes(estado)} />
															<SoftTypography variant="button" color="dark">{estado}</SoftTypography>
														</MenuItem>
													))}
												</Select>
											</SoftBox>

											<SoftButton
												variant="outlined"
												color="dark"
												disabled={!filtersAreActive}
												onClick={handleResetFilters}
												sx={{
													minHeight: 40,
													whiteSpace: "nowrap",
													gridColumn: { xs: "1", sm: "1 / -1", xl: "auto" },
												}}
											>
												<Icon sx={{ mr: 0.75 }}>filter_alt_off</Icon>
												Limpiar
											</SoftButton>
										</SoftBox>
									</SoftBox>

									<Dialog
										open={openNoAprobado2}
										onClose={handleCloseModalNoAprobado}
										sx={{ "& .MuiDialog-paper": { borderRadius: "16px" } }}
									>
										<DialogContent>
											<NoAprobado2 />
										</DialogContent>
										<DialogActions sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
											<SoftButton onClick={handleCloseModalNoAprobado} color="primary">
												Cerrar
											</SoftButton>
										</DialogActions>
									</Dialog>

									<SoftBox
										sx={{
											mx: { xs: 2, sm: 3 },
											mb: 3,
											border: { xs: "none", lg: "1px solid #e9ecef" },
											borderRadius: 2,
											overflow: "hidden",
										}}
									>
										{loading || loadingProveedores ? (
											<Loading />
										) : !dataProveedor?.proveedorUser.habilitado ? (
											<NoAprobado />
										) : rows?.length > 0 ? (
											<>
												<SoftBox display={{ xs: "none", lg: "block" }}>
													<DataTable columns={columns} rows={rows} order={order} setOrder={handleOrder} />
												</SoftBox>
												<SoftBox display={{ xs: "flex", lg: "none" }} flexDirection="column" gap={2}>
													{data?.pedidosCompra?.map((item) => (
														<CotizacionCard
															key={item.id}
															item={item}
															idProveedor={dataProveedor?.proveedorUser?.id}
															colores={colores}
															onSelect={setDrawerCotizacion}
														/>
													))}
												</SoftBox>
											</>
										) : (
											<SinDatos />
										)}
										{!(dataProveedor?.proveedorUser?.categorias?.length > 0) && (
											<SoftBox display="flex" justifyContent="center" alignItems="center" color="warning" gap={0.5}>
												<Icon>warning</Icon>
												<SoftTypography variant="caption" color="secondary">
													No tiene rubros asignados
												</SoftTypography>
											</SoftBox>
										)}
									</SoftBox>
									{dataProveedor?.proveedorUser.habilitado && (
										<CustomPagination
											loading={loading}
											length={data?.pedidosCompra.length}
											total={pagesInfo?.total}
											pages={pagesInfo?.pages}
											page={page}
											setPage={setPage}
											noCard
										/>
									)}
								</Card>
								<CotizacionDrawer
									item={drawerCotizacion}
									open={Boolean(drawerCotizacion)}
									onClose={() => setDrawerCotizacion(null)}
									onOpenCotizacion={(item) => {
										setDrawerCotizacion(null);
										setSelected(item);
									}}
									idProveedor={dataProveedor?.proveedorUser?.id}
									colores={colores}
								/>
							</SoftBox>

							<SoftBox
								role="tabpanel"
								id="licitaciones-tabpanel-1"
								aria-labelledby="licitaciones-tab-1"
								hidden={activeSectionTab !== 1}
								sx={{ "&[hidden]": { display: "none" } }}
							>
								<CalendarioEntregas
									idProveedor={dataProveedor?.proveedorUser?.id ?? null}
									solicitudes={dataSolicitudes?.solicitudesEntregaProveedor?.solicitudes || []}
									loadingSolicitudes={loadingSolicitudes}
									provisiones={dataProvisiones?.provisiones || []}
									loadingProvisiones={loadingProvisiones}
									onOpenRemitos={handleOpenRemitos}
									onGeneratePdf={handleGeneratePdf}
									forceOpenCollapse
								/>
							</SoftBox>

							<SoftBox
								role="tabpanel"
								id="licitaciones-tabpanel-2"
								aria-labelledby="licitaciones-tab-2"
								hidden={activeSectionTab !== 2}
								sx={{ "&[hidden]": { display: "none" } }}
							>
								<FacturacionTab
									loading={loadingFacturacion}
									provisiones={provisionesFacturacion}
									resumen={facturacionResumen}
									search={facturacionFilters.search}
									estado={facturacionFilters.estado}
									fecha={facturacionFilters.fecha}
									onSearchChange={(value) => {
										setFacturacionFilters((prev) => ({ ...prev, search: value }));
										setFacturacionPage(1);
									}}
									onEstadoChange={(value) => {
										setFacturacionFilters((prev) => ({ ...prev, estado: value }));
										setFacturacionPage(1);
									}}
									onFechaChange={(value) => {
										setFacturacionFilters((prev) => ({ ...prev, fecha: value }));
										setFacturacionPage(1);
									}}
									onClearFilters={handleClearFacturacionFilters}
									expandedProvisions={expandedProvisions}
									onToggleProvision={toggleProvisionExpansion}
									remitosColumns={remitosColumns}
									remitosActions={remitosActions}
									loadingFactura={loadingFactura}
									pagination={facturacionPagination}
									onPageChange={setFacturacionPage}
								/>
							</SoftBox>
						</>
					)}
				</SoftBox>
				<ModalRemitos
					open={openModalRemitos}
					handleClose={() => setOpenModalRemitos(false)}
					idProvision={idProvision}
					provisionData={selectedProvision}
				/>
				<DinamicModal
					open={remitoRechazado !== null}
					handleClose={() => {
						setRemitoRechazado(null);
					}}
					title="Motivo del rechazo de la factura"
					data={{
						Mensaje: remitoRechazado?.pagosProveedor?.motivoRechazo || "No se proporcionó un motivo.",
					}}
				/>
			</SoftBox>
			{pdfData && (
				<SoftBox sx={{ display: "none" }}>
					<EntregaPDF data={pdfData} onGeneratePdf={handlePdfGenerated} />
				</SoftBox>
			)}
			<Footer />
		</DashboardLayout>
	);
}

export default Licitaciones;
